<form [formGroup]="viewForm">
  <div class="vflex-200">
    <h2>Selects</h2>
    <p>
      Selects are our custom implementation of dropdowns, with additionsl functionality like search,
      grouping, single/multi select, keyboard navigation, and more.
    </p>
    <p>
      The select component is built on top of Angular Aria's Listbox component, which provides a
      signal-based foundation for building custom select components. The dropdown uses virtual
      scrolling for performance with large lists, and options are styled with Meadow's ListItem.
    </p>
    <h3>Basic Select</h3>
    <p>This shows a simple custom version of a basic dropdown list</p>
    <ng-template #basicSelect data-sample="basicSelect">
      <div class="vflex-200">
        <mdw-select
          label="State"
          description="Select the state where your primary work hours occur"
          [options]="basicOptions"
          [placeholder]="'Select an option'"
          formControlName="state"
        ></mdw-select>
        <div>
          <button class="mdw-button mdw-primary" (click)="selectState('MD')">Select 'MD'</button>
        </div>
      </div>
    </ng-template>
    <app-sample [sampleContent]="basicSelect" sampleName="basicSelect"></app-sample>

    <h3>Basic Multi Select</h3>
    <p>
      This shows a simple custom version of a basic dropdown list but it allows multiple selected
      options
    </p>
    <ng-template #basicMultiSelect data-sample="basicMultiSelect">
      <div class="vflex-200">
        <mdw-select
          label="State"
          description="Select the state where your primary work hours occur"
          [options]="basicOptions"
          [placeholder]="'Select an option'"
          [multiSelect]="true"
          formControlName="states"
        ></mdw-select>

        <div>
          <button class="mdw-button mdw-primary" (click)="selectStates(['WA', 'GA'])">
            Select 'WA' and 'GA'
          </button>
        </div>
      </div>
    </ng-template>
    <app-sample [sampleContent]="basicMultiSelect" sampleName="basicMultiSelect"></app-sample>

    <h3>Rich Object List with Custom Templates</h3>
    <p>
      By default, the select component accepts an array of strings, but it can also support objects.
      For This to work you need to either provide a custom template, or provide properties for the
      label and value of the option.
    </p>
    <h4>Label and Value fields</h4>
    <p>
      The select component can accept
      <code>labelProperty</code>
      (default: 'label') and
      <code>valueProperty</code>
      (default: 'value').
    </p>
    <ng-template #labelAndValueSelect data-sample="labelAndValueSelect">
      <div class="vflex-200">
        <mdw-select
          label="State Abbreviation as Value"
          description="Select the state where your primary work hours occur"
          [options]="objectOptions"
          [placeholder]="'Select an option'"
          labelProperty="name"
          valueProperty="abbreviation"
          formControlName="richStateAbbreviation"
        ></mdw-select>
        <mdw-select
          label="Entire State Object as Value"
          description="Select the state where your primary work hours occur"
          [options]="objectOptions"
          [placeholder]="'Select an option'"
          labelProperty="name"
          formControlName="richState"
        ></mdw-select>
      </div>
    </ng-template>
    <app-sample [sampleContent]="labelAndValueSelect" sampleName="labelAndValueSelect"></app-sample>

    <h3>Select with Item Template</h3>
    <p>
      The select component can also accept a custom template for each item in the list. This is
      useful when you need to display more than just a simple string in the dropdown.
    </p>
    <p>
      You should still provide a labelProperty so the selected value is displayed correctly in the
      input field.
    </p>
    <ng-template #templateItemSelect data-sample="templateItemSelect">
      <mdw-select
        label="State Abbreviation"
        description="Select the state where your primary work hours occur"
        [options]="objectOptions"
        [placeholder]="'Select a state from the list'"
        labelProperty="name"
        valueProperty="abbreviation"
        formControlName="templateStateAbbreviation"
        [virtualItemSize]="64"
        [templateRef]="itemTemplate"
      ></mdw-select>
      <ng-template let-option #itemTemplate>
        <div class="pad-100">
          <div>{{ option.name }}</div>
          <div class="secondary">
            {{ option.abbreviation }}
            <small>{{ option.statehoodDate }}</small>
          </div>
        </div>
      </ng-template>
    </ng-template>
    <app-sample [sampleContent]="templateItemSelect" sampleName="templateItemSelect"></app-sample>

    <h3>Select with Selected Item Template</h3>
    <p>
      The select component can also accept a custom template for each selected item in the trigger.
      This is useful when you need to display more than just a simple string in the trigger.
    </p>
    <p>
      You can optionally allow the selected items to be dismissed from the trigger (defaults to
      true)
    </p>
    <ng-template #selectedItemTemplateItemSelect data-sample="selectedItemTemplateItemSelect">
      <mdw-select
        label="State Abbreviation"
        description="Select the state where your primary work hours occur"
        [options]="objectOptions"
        [placeholder]="'Select a state from the list'"
        labelProperty="name"
        valueProperty="abbreviation"
        formControlName="templateStateAbbreviation"
        [multiSelect]="true"
        [virtualItemSize]="42"
        [templateRef]="itemTemplate"
        [selectedItemTemplateRef]="itemTemplate"
      ></mdw-select>
      <ng-template let-option #itemTemplate>
        <div class="pad-100 hflex-050">
          <div>{{ option.name }}</div>
          <div class="secondary">
            {{ option.abbreviation }}
            <small>{{ option.statehoodDate }}</small>
          </div>
        </div>
      </ng-template>
    </ng-template>
    <app-sample
      [sampleContent]="selectedItemTemplateItemSelect"
      sampleName="selectedItemTemplateItemSelect"
    ></app-sample>

    <h3>Large List with Virtual Scrolling</h3>
    <p>
      The select component uses virtual scrolling to efficiently handle large datasets. Only visible
      items are rendered to the DOM, so even 10,000 items open instantly. Use the
      <code>virtualItemSize</code>
      input to match the pixel height of each row when using custom templates.
    </p>
    <ng-template #largeListSelect data-sample="largeListSelect">
      <mdw-select
        label="User"
        description="Select a user from 10,000 items"
        [options]="largeListOptions"
        [placeholder]="'Search for a user'"
        labelProperty="name"
        valueProperty="id"
        [enableSearch]="true"
        [searchProperties]="['name', 'email', 'department']"
        [virtualItemSize]="78"
        [menuMaxHeight]="350"
        formControlName="largeListUser"
        [templateRef]="largeListItemTemplate"
      ></mdw-select>
      <ng-template let-option #largeListItemTemplate>
        <div class="pad-100 vflex-050">
          <div class="bold">{{ option.name }}</div>
          <div class="extra-small secondary">{{ option.email }}</div>
          <div class="extra-small secondary">{{ option.department }}</div>
        </div>
      </ng-template>
    </ng-template>
    <app-sample [sampleContent]="largeListSelect" sampleName="largeListSelect"></app-sample>

    <h3>Select with Search</h3>
    <p>
      Enable the
      <code>enableSearch</code>
      input to show a search input at the top of the dropdown. This is useful for large lists where
      users need to quickly find an option. Keyboard navigation is fully supported: arrow keys to
      move, Enter/Tab to select, and Escape to close.
    </p>
    <ng-template #searchableSelect data-sample="searchableSelect">
      <mdw-select
        label="State"
        description="Search for a state by name or abbreviation"
        [options]="objectOptions"
        [placeholder]="'Select a state'"
        labelProperty="name"
        valueProperty="abbreviation"
        [enableSearch]="true"
        [searchProperties]="['name', 'abbreviation']"
        formControlName="searchableState"
      ></mdw-select>
    </ng-template>
    <app-sample [sampleContent]="searchableSelect" sampleName="searchableSelect"></app-sample>

    <h3>Multi Select with Search</h3>
    <p>
      Combine
      <code>enableSearch</code>
      with
      <code>multiSelect</code>
      to allow searching and selecting multiple options. Use arrow keys to navigate, Enter to toggle
      selection, and Tab to close.
    </p>
    <ng-template #searchableMultiSelect data-sample="searchableMultiSelect">
      <mdw-select
        label="States"
        description="Search and select multiple states"
        [options]="objectOptions"
        [placeholder]="'Select states'"
        labelProperty="name"
        valueProperty="abbreviation"
        [enableSearch]="true"
        [multiSelect]="true"
        [searchProperties]="['name', 'abbreviation']"
        formControlName="searchableStates"
      ></mdw-select>
    </ng-template>
    <app-sample
      [sampleContent]="searchableMultiSelect"
      sampleName="searchableMultiSelect"
    ></app-sample>

    <div class="surface-muted border-radius-200 pad-200">
      <b>Form Value:</b>
      {{ viewForm.value | json }}
    </div>
  </div>
</form>
